import React, { useEffect, useState } from "react";
import { api, brl } from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Tag, Trash2 } from "lucide-react";

export default function DiscountDialog({ target, onClose, onSaved }) {
  const initial = target?.discount || {};
  const [form, setForm] = useState({
    kind: "fixed", value: 0, duration: "permanent", months: 1, note: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) {
      const d = target.discount || {};
      setForm({
        kind: d.kind || "fixed",
        value: d.value || 0,
        duration: d.duration || "permanent",
        months: d.original_months || d.months_remaining || 1,
        note: d.note || "",
      });
    }
  }, [target]);

  if (!target) return null;

  const basePrice = target.subscription_status?.plan_price ?? 0;
  const previewFinal = (() => {
    if (!form.value || form.value <= 0) return basePrice;
    if (form.kind === "fixed") return Math.max(0, basePrice - Number(form.value));
    if (form.kind === "percent") return Math.max(0, basePrice * (1 - Number(form.value) / 100));
    return basePrice;
  })();

  const submit = async () => {
    if (!form.value || Number(form.value) <= 0) {
      toast.error("Informe um valor maior que zero");
      return;
    }
    if (form.kind === "percent" && Number(form.value) > 100) {
      toast.error("Percentual não pode ser > 100");
      return;
    }
    if (form.duration === "months" && (!form.months || Number(form.months) <= 0)) {
      toast.error("Informe quantos meses (>0)");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/master/tenants/${target.id}/discount`, {
        kind: form.kind,
        value: Number(form.value),
        duration: form.duration,
        months: form.duration === "months" ? Number(form.months) : null,
        note: form.note,
      });
      toast.success("Desconto aplicado");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao aplicar desconto");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remover o desconto desta loja?")) return;
    setBusy(true);
    try {
      await api.delete(`/master/tenants/${target.id}/discount`);
      toast.success("Desconto removido");
      onSaved();
    } catch (e) {
      toast.error("Erro ao remover");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:w-auto sm:max-w-md max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="discount-dialog">
        <DialogHeader>
          <DialogTitle>Desconto — {target.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-xs p-3 rounded bg-neutral-50 border border-[#E5E5E5]">
            <p className="text-neutral-500">Plano atual: <span className="font-medium text-neutral-700">{target.subscription_status?.plan_label}</span></p>
            <p className="text-neutral-500">Preço base: <span className="font-medium text-neutral-700">{brl(basePrice)}</span></p>
            {form.value > 0 && (
              <p className="mt-1 text-emerald-700 font-medium">
                Preço com desconto: <span className="line-through text-neutral-400 mr-1">{brl(basePrice)}</span>{brl(previewFinal)} / mês
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo</Label>
              <Select value={form.kind} onValueChange={(v)=>setForm(p=>({...p, kind: v}))}>
                <SelectTrigger data-testid="discount-kind-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixo (R$)</SelectItem>
                  <SelectItem value="percent">Percentual (%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{form.kind === "percent" ? "Percentual (%)" : "Valor (R$)"}</Label>
              <Input type="number" min="0" step={form.kind === "percent" ? "1" : "0.01"} value={form.value}
                onChange={(e)=>setForm(p=>({...p, value: e.target.value}))} data-testid="discount-value-input"/>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Duração</Label>
              <Select value={form.duration} onValueChange={(v)=>setForm(p=>({...p, duration: v}))}>
                <SelectTrigger data-testid="discount-duration-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="permanent">Permanente</SelectItem>
                  <SelectItem value="months">Por X meses</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.duration === "months" && (
              <div>
                <Label>Meses</Label>
                <Input type="number" min="1" value={form.months}
                  onChange={(e)=>setForm(p=>({...p, months: e.target.value}))} data-testid="discount-months-input"/>
              </div>
            )}
          </div>

          <div>
            <Label>Nota interna (opcional)</Label>
            <Textarea rows={2} value={form.note} onChange={(e)=>setForm(p=>({...p, note: e.target.value}))}
              placeholder="Ex.: Black Friday, fidelidade, parceiro…"/>
          </div>
        </div>

        <DialogFooter className="gap-2">
          {initial.kind && (
            <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={remove} disabled={busy} data-testid="discount-remove-button">
              <Trash2 size={14} className="mr-1"/> Remover
            </Button>
          )}
          <div className="flex-1"/>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="discount-save-button">
            <Tag size={14} className="mr-1"/> {busy ? "Salvando…" : "Aplicar desconto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
